import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { useInView } from 'react-intersection-observer'
import { useTenantThemeConfig } from '../hooks/useTenantThemeConfig'
import { getImageUrl } from '../utils/cdnUtils'
import { canRoundAcceptBet, isRoundNotYetOpen, lotteryService, LotteryRound, PublicHuayType } from '../services/lotteryService'

// ✨ Motion Variants for Animations
const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 },
}

const cardVariants = {
  hidden: { opacity: 0, y: 100, scale: 0.8 },
  visible: { opacity: 1, y: 0, scale: 1 },
  hover: { scale: 1.05, y: -5 },
}

/** แสดง countdown ที่อัปเดตทุกวินาที — ยังไม่เปิด: นับถอยหลังถึงเวลาเปิด, เปิดอยู่: นับถึงเวลาปิด, ปิดแล้ว: "ปิดรับแทง" */
const computeCountdownText = (round: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>) => {
  if (isRoundNotYetOpen(round)) {
    return lotteryService.formatTimeRemaining(round.betStartTime)
  }
  return canRoundAcceptBet(round) ? lotteryService.formatTimeRemaining(round.betEndTime) : 'ปิดรับแทง'
}

const CountdownDisplay: React.FC<{ round: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'> }> = ({ round }) => {
  const [text, setText] = useState(() => computeCountdownText(round))

  useEffect(() => {
    const tick = () => {
      setText(computeCountdownText(round))
    }

    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [round])

  return <>{text}</>
}

function normalizeLabel(value: string): string {
  return String(value || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/[().]/g, '')
    .toLowerCase()
}

function formatCloseTime(betEndTime: string): string {
  const betEndDate = new Date(betEndTime)
  return betEndDate.toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ✨ LotteryCard Component with Motion (หวยอื่นๆ — ใช้หน่วยนาที + config wipWupMinutesOther)
interface LotteryCardProps {
  round: LotteryRound
  index: number
  navigate: any
  route?: string
  /** นาทีที่เหลือก่อนปิดรับ — แสดงวิบวับเมื่อน้อยกว่าค่านี้ (จากแอดมิน ตั้งค่าอื่นๆ) */
  urgentMinutesThreshold?: number
}

const LotteryCard: React.FC<LotteryCardProps> = ({ round, index, navigate, route = '/lottery-terms', urgentMinutesThreshold = 15 }) => {
  const notYetOpen = isRoundNotYetOpen(round)
  const isActive = canRoundAcceptBet(round) && !notYetOpen
  const betEndDate = new Date(round.betEndTime)
  const closeTime = formatCloseTime(round.betEndTime)
  const openTime = formatCloseTime(round.betStartTime)

  // 🔥 นาทีที่เหลือก่อนปิดรับ — แสดงวิบวับเมื่อน้อยกว่าค่าจาก config (สอดคล้อง user-modern)
  const now = new Date()
  const minutesRemaining = (betEndDate.getTime() - now.getTime()) / (1000 * 60)
  const isUrgent = isActive && minutesRemaining <= urgentMinutesThreshold && minutesRemaining > 0

  // 🎨 Dynamic styling
  const cardStyle: React.CSSProperties = {
    ...(isUrgent && {
      border: '3px solid #ffa500',
      boxShadow: '0 0 20px rgba(255, 165, 0, 0.6), 0 0 40px rgba(255, 165, 0, 0.4)',
      animation: 'pulse-glow 2s ease-in-out infinite',
    }),
  }

  return (
    <>
      <style>{`
        @keyframes pulse-glow {
          0%, 100% { 
            box-shadow: 0 0 20px rgba(255, 165, 0, 0.6), 0 0 40px rgba(255, 165, 0, 0.4);
          }
          50% { 
            box-shadow: 0 0 30px rgba(255, 165, 0, 0.8), 0 0 60px rgba(255, 165, 0, 0.6);
          }
        }
      `}</style>
      <motion.div
        className="col-6 col-md-4 col-lg-3 px-1 mt-2"
        initial="hidden"
        animate="visible"
        whileHover={isActive ? "hover" : undefined}
        variants={cardVariants}
        transition={{ 
          duration: 0.6, 
          delay: 0.1 + index * 0.08,
          ease: [0.43, 0.13, 0.23, 0.96]
        }}
      >
        <a
          href="#"
          className={`${isActive ? 'bgg-4' : 'bgc-4'} bdr-5 text-center box-hover h-100`}
          style={{
            display: 'block',
            textDecoration: 'none',
            cursor: 'pointer',
            ...cardStyle,
          }}
          onClick={(e) => {
            e.preventDefault()
            if (notYetOpen) {
              toast.error('ยังไม่เปิดรับแทง รอเวลาเปิด')
              return
            }
            if (isActive) {
              navigate(route, { state: { round } })
            } else {
              // รอบปิดรับแล้ว → ไปหน้าผลรางวัล (หวยทั่วไป ไม่ใช่ยี่กี)
              const name = (round.huayTypeName || '') + (round.huayName || '')
              if (!/ยี่กี|ยี/.test(name)) {
                navigate(`/lottery-result?roundId=${round._id}`, { state: { round } })
              }
            }
          }}
        >
          <div className={`px-1 py-1 t-regular ${isActive ? 'c-white' : notYetOpen ? 'c-dark' : 'c-red'}`}>
            {notYetOpen && <small className="d-block" style={{ fontWeight: 600 }}>ยังไม่เปิดรับแทง</small>}
            <h5 style={{ fontWeight: isUrgent ? '700' : '400' }}><CountdownDisplay round={round} /></h5>
          </div>
          <div className="bgc-14 px-1 t-regular c-white">
            <h4 className="txt-hh">{round.huayName}</h4>
          </div>
          <div className={`px-1 py-2 ${isActive ? 'c-white' : 'c-dark'}`}>
            <small>{notYetOpen ? `เปิดรับ ${openTime}` : `ปิดรับ ${closeTime}`}</small>
          </div>
        </a>
      </motion.div>
    </>
  )
}

// ✨ LotterySection Component with InView Detection
interface LotterySectionProps {
  title: string
  titleBgColor?: string
  icon?: string
  children: React.ReactNode
}

const LotterySection: React.FC<LotterySectionProps> = ({ 
  title, 
  titleBgColor = 'bgc-14',
  icon = 'logo.png',
  children 
}) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  return (
    <div className="col-12 mt-2 px-1">
      <motion.div
        ref={ref}
        className={`${titleBgColor} bdr-5 p-2 h-100`}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={sectionVariants}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <motion.div 
          className="wrap animateMe" 
          data-animation="fadeInUp"
          initial={{ opacity: 0, x: -20 }}
          animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="icon-f">
            <img src={/^([a-z]+:)?\/\//i.test(icon) || icon.startsWith('data:') ? icon : getImageUrl(icon)} alt={title} />
          </div>
          <h5 className="t-medium mx-2 c-white">{title}</h5>
        </motion.div>

        <div className="wrap-box">
          <div className="row mx--1">
            {children}
          </div>
        </div>
      </motion.div>
    </div>
  )
}

const LottoPage: React.FC = () => {
  const { themeConfig } = useTenantThemeConfig()
  const navigate = useNavigate()
  /** นาทีที่เหลือก่อนปิดรับ — แสดงวิบวับหวยอื่นๆ (จากแอดมิน ตั้งค่าอื่นๆ) */
  const urgentMinutesThreshold = themeConfig?.settings?.wipWupMinutesOther ?? 15
  const [rounds, setRounds] = useState<LotteryRound[]>([])
  const [huayTypes, setHuayTypes] = useState<PublicHuayType[]>([])
  const [loading, setLoading] = useState(true)
  const isInitialLoadRef = useRef(true)
  /** ประเภทหวยที่แอดมินปิด (จาก WebSocket huay_type_updated) — ซ่อน section ทั้งบล็อก */
  const [disabledHuayTypeNames, setDisabledHuayTypeNames] = useState<Set<string>>(() => new Set())

  // Load today's rounds — แสดง loading เฉพาะครั้งแรก, refresh หลังๆ อัปเดตในพื้นหลังโดยไม่เด้งหน้า
  const loadRounds = useCallback(async (cacheBust = false) => {
    const showLoading = isInitialLoadRef.current
    if (showLoading) {
      isInitialLoadRef.current = false
      setLoading(true)
    }
    try {
      const todayRounds = await lotteryService.getTodayRounds(cacheBust)
      // ต้อง setRounds เสมอ — ห้าม return prev เพราะ status อาจเปลี่ยนจาก WebSocket (prevIds เท่ากันแต่ status ไม่เท่า)
      setRounds(todayRounds)
    } catch (error) {
      console.error('❌ Error loading rounds:', error)
    } finally {
      if (showLoading) setLoading(false)
    }
  }, [])

  const loadHuayTypes = useCallback(async () => {
    try {
      const list = await lotteryService.getTenantHuayTypes({ limit: 100 })
      setHuayTypes(list)
    } catch (error) {
      console.error('❌ Error loading huay types:', error)
    }
  }, [])

  useEffect(() => {
    loadHuayTypes()
    loadRounds()
    const intervalId = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        loadRounds()
      }
    }, 30000)
    return () => clearInterval(intervalId)
  }, [loadHuayTypes, loadRounds])

  // อัปเดตรอบหวยแบบ real-time เมื่อ admin แก้ status (socket lottery_round_updated) หรือเปิด/ปิดประเภทหวย (huay_type_updated)
  useEffect(() => {
    const handleRoundUpdated = (e: Event) => {
      const customEvent = e as CustomEvent<{ round: LotteryRound; tenantId?: string }>
      const updated = customEvent.detail?.round
      if (!updated?._id) return
      loadRounds(true)
    }
    const handleHuayTypeUpdated = (e: Event) => {
      const detail = (e as CustomEvent<{ name?: string; isActive?: boolean }>)?.detail
      const name = detail?.name
      if (name) {
        setDisabledHuayTypeNames((prev) => {
          const next = new Set(prev)
          if (detail.isActive === false) next.add(name)
          else if (detail.isActive === true) next.delete(name)
          return next
        })
      }
      loadHuayTypes()
      loadRounds(true)
    }
    window.addEventListener('lottery_round_updated', handleRoundUpdated)
    window.addEventListener('huay_type_updated', handleHuayTypeUpdated)
    return () => {
      window.removeEventListener('lottery_round_updated', handleRoundUpdated)
      window.removeEventListener('huay_type_updated', handleHuayTypeUpdated)
    }
  }, [loadHuayTypes, loadRounds])

  const huayTypeImageMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const huayType of huayTypes) {
      const image = String(huayType.imageUrl || huayType.imageURL || '').trim()
      if (!image) continue
      if (huayType.name) map.set(normalizeLabel(huayType.name), image)
      if (huayType.code) map.set(normalizeLabel(huayType.code), image)
    }
    return map
  }, [huayTypes])

  const getHuayTypeImageSrc = useCallback(
    (aliases: string[], fallbackAsset: string) => {
      for (const alias of aliases) {
        const matched = huayTypeImageMap.get(normalizeLabel(alias))
        if (matched) {
          return matched
        }
      }
      return getImageUrl(fallbackAsset)
    },
    [huayTypeImageMap]
  )

  // Group rounds by type — เปิดรับ (เรียงตามเวลาปิดรับ) → ยังไม่เปิด → ปิดรับแล้ว (ท้ายสุด)
  const roundsByType = useMemo(() => {
    const grouped = lotteryService.groupRoundsByType(rounds)
    for (const key of Object.keys(grouped)) {
      grouped[key] = lotteryService.sortRoundsForLottoDisplay(grouped[key])
    }
    return grouped
  }, [rounds])

  /** ประเภทที่มี section เฉพาะแล้ว — ประเภทที่เหลือจาก API จะไปแสดงในบล็อก "dynamic" ด้านล่าง */
  const KNOWN_SECTION_TYPE_NAMES = useRef(
    new Set([
      'หวยรัฐบาลไทย',
      'หวยยี่กี',
      'หวยยีกี่',
      'ยี่กี',
      'ยีกี่',
      'หวยต่างประเทศ',
      'หวยหุ้น VIP',
      'หวย VIP',
      'หวยหุ้น',
      'หวยชุด',
      'หวย VISA',
    ])
  ).current
  const otherTypeNames = Object.keys(roundsByType).filter((k) => !KNOWN_SECTION_TYPE_NAMES.has(k))

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row mx--1">
          <div className="col-12 px-1">
            <div className="w-100 p-2 bgc-4 bdr-5 wrap justify-content-between">
              <a 
                href="#" 
                className="txt-link-d"
                onClick={(e) => {
                  e.preventDefault()
                  navigate('/dashboard')
                }}
              >
                <i className="fas fa-chevron-left me-1"></i>
                หน้าหลัก
              </a>
              <a 
                href="#" 
                className="txt-link-d"
                onClick={(e) => {
                  e.preventDefault()
                  navigate('/lottery-result')
                }}
              >
                <i className="fas fa-trophy me-1"></i>
                ผลรางวัล
              </a>
            </div>
          </div>

          {/* หวยรัฐบาล - Dynamic from API */}
          <div className="col-6 col-md-6 mt-2 px-1">
            <div className="bgc-14 bdr-5 p-2 h-100">
              <div className="wrap animateMe" data-animation="fadeInUp">
                <div className="icon-f">
                  <img src={getHuayTypeImageSrc(['หวยรัฐบาลไทย'], 'flag/th.png')} alt="หวยรัฐบาล" />
                </div>
                <h5 className="t-medium mx-2 c-white">หวยรัฐบาล</h5>
              </div>

              <div className="wrap-box">
                <div className="row mx--1">
                  {loading ? (
                    <div className="col-12 px-1 mt-2 animateMe" data-animation="fadeInUp">
                      <div className="text-center py-4">
                        <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
                      </div>
                    </div>
                  ) : (() => {
                    // Filter เฉพาะ "หวยรัฐบาลไทย" ตัวจริง (ไม่ใช่ ออมสิน หรือ ธกส)
                    const govRounds = (roundsByType['หวยรัฐบาลไทย'] || []).filter(r => 
                      r.huayName.includes('รัฐบาลไทย') && 
                      !r.huayName.includes('ออมสิน') && 
                      !r.huayName.includes('ธกส') &&
                      !r.huayName.includes('ธ.ก.ส.')
                    )
                    
                    return govRounds.length > 0 ? (
                      govRounds.slice(0, 1).map((round) => {
                      const notYetOpen = isRoundNotYetOpen(round)
                      const isActive = canRoundAcceptBet(round) && !notYetOpen
                      const closeTime = formatCloseTime(round.betEndTime)
                      const openTime = formatCloseTime(round.betStartTime)
                      
                      return (
                        <div
                          key={round._id}
                          className="col-12 px-1 mt-2 animateMe"
                          data-animation="fadeInUp"
                        >
                          <a
                            href="#"
                            className={`${isActive ? 'bgg-4' : 'bgc-4'} bdr-5 text-center box-hover h-100`}
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.preventDefault()
                              if (notYetOpen) {
                                toast.error('ยังไม่เปิดรับแทง รอเวลาเปิด')
                                return
                              }
                              if (isActive) {
                                navigate('/lottery-terms', { state: { round } })
                              } else {
                                navigate(`/lottery-result?roundId=${round._id}`, { state: { round } })
                              }
                            }}
                          >
                            <div className={`px-1 py-1 t-regular ${isActive ? 'c-white' : notYetOpen ? 'c-dark' : 'c-red'}`}>
                              {notYetOpen && <small className="d-block" style={{ fontWeight: 600 }}>ยังไม่เปิดรับแทง</small>}
                              <h5><CountdownDisplay round={round} /></h5>
                            </div>
                            <div className="bgc-14 px-1 t-regular c-white">
                              <h4 className="txt-hh">{round.huayName}</h4>
                            </div>
                            <div className={`px-1 py-2 ${isActive ? 'c-white' : 'c-dark'}`}>
                              <small>{notYetOpen ? `เปิดรับ ${openTime}` : `ปิดรับ ${closeTime}`}</small>
                            </div>
                          </a>
                        </div>
                      )
                    })
                  ) : (
                    <div className="col-12 px-1 mt-2 animateMe" data-animation="fadeInUp">
                      <a
                        href="#"
                        className="bgc-4 bdr-5 text-center box-hover h-100"
                        onClick={(e) => e.preventDefault()}
                      >
                        <div className="px-1 py-1 t-regular c-red">
                          <h5>ปิดรับแทง</h5>
                      </div>
                      <div className="bgc-14 px-1 t-regular c-white">
                        <h4 className="txt-hh">หวยรัฐบาล</h4>
                      </div>
                        <div className="px-1 py-2 c-dark">
                          <small>ไม่มีรอบหวย</small>
                      </div>
                    </a>
                  </div>
                  )
                })()}
                </div>
              </div>
            </div>
          </div>

          {/* จับยี่กี - Dynamic with Motion */}
          <div className="col-6 col-md-6 mt-2 px-1">
            <motion.div 
              className="bgc-14 bdr-5 p-2 h-100"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <motion.div 
                className="wrap animateMe" 
                data-animation="fadeInUp"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="icon-f">
                  <img src={getHuayTypeImageSrc(['หวยยี่กี', 'หวยยีกี่', 'ยี่กี', 'ยีกี่'], 'logo.png')} alt="จับยี่กี" />
                </div>
                <h5 className="t-medium mx-2 c-white">จับยี่กี</h5>
              </motion.div>

              <div className="wrap-box">
                <div className="row mx--1">
                  <motion.div
                    className="col-12 px-1 mt-2 animateMe"
                    data-animation="fadeInUp"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                  >
                    <a
                      href="#"
                      className="bgg-1 bdr-5 text-center box-hover h-100"
                      style={{ display: 'block', textDecoration: 'none' }}
                      onClick={(e) => {
                        e.preventDefault()
                        navigate('/yeekee')
                      }}
                    >
                      <div className="px-1 py-1 t-regular c-white">
                        <h5><i className="fas fa-history me-2"></i>24 ชั่วโมง</h5>
                      </div>
                      <div className="bgc-14 px-1 t-regular c-white">
                        <h4 className="txt-hh">จับยี่กี</h4>
                      </div>
                      <div className="px-1 py-2 c-white">
                        <small>
                          {loading ? (
                            <i className="fas fa-spinner fa-spin"></i>
                          ) : (
                            <>
                              เปิดแทง {(
                                roundsByType['หวยยี่กี'] || 
                                roundsByType['หวยยีกี่'] || 
                                roundsByType['ยี่กี'] || 
                                roundsByType['ยีกี่'] || 
                                []
                              ).length} รอบ
                            </>
                          )}
                        </small>
                      </div>
                    </a>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* หวยธนาคาร - Dynamic from API */}
          <div className="col-12 mt-2 px-1">
            <div className="bgc-14 bdr-5 p-2 h-100">
              <div className="wrap animateMe" data-animation="fadeInUp">
                <div className="icon-f">
                  <img src={getHuayTypeImageSrc(['หวยธนาคาร'], 'logo.png')} alt="หวยธนาคาร" />
                </div>
                <h5 className="t-medium mx-2 c-white">หวยธนาคาร</h5>
              </div>

              <div className="wrap-box">
                <div className="row mx--1">
                  {loading ? (
                    <div className="col-12 text-center py-4">
                      <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
                    </div>
                  ) : (
                    // Filter รอบหวยธนาคาร: ธ.ก.ส., ออมสิน
                    (() => {
                      // Define bank lotteries to always show
                      const bankLotteryConfigs = [
                        { searchNames: ['ธ.ก.ส.', 'ธกส'], displayName: 'หวย ธ.ก.ส.' },
                        { searchNames: ['ออมสิน'], displayName: 'หวยออมสิน' }
                      ]
                      
                      return bankLotteryConfigs.map((config, idx) => {
                        // Find round for this bank lottery
                        const round = rounds.find(r => 
                          config.searchNames.some(name => r.huayName.includes(name))
                        )
                        
                        if (!round) {
                          // ไม่มีรอบ - แสดง "ไม่มีรอบหวย"
                          return (
                            <div key={`bank-${idx}`} className="col-6 col-md-6 px-1 mt-2 animateMe" data-animation="fadeInUp">
                              <a href="#" className="bgc-4 bdr-5 text-center box-hover h-100" onClick={(e) => e.preventDefault()}>
                                <div className="px-1 py-1 t-regular c-red">
                                  <h5>ปิดรับแทง</h5>
                                </div>
                                <div className="bgc-14 px-1 t-regular c-white">
                                  <h4 className="txt-hh">{config.displayName}</h4>
                                </div>
                                <div className="px-1 py-2 c-dark">
                                  <small>ไม่มีรอบหวย</small>
                                </div>
                              </a>
                            </div>
                          )
                        }
                        
                        const notYetOpen = isRoundNotYetOpen(round)
                        const isActive = canRoundAcceptBet(round) && !notYetOpen
                        const closeTime = formatCloseTime(round.betEndTime)
                        const openTime = formatCloseTime(round.betStartTime)
                        
                        return (
                          <div
                            key={round._id}
                            className="col-6 col-md-6 px-1 mt-2 animateMe"
                            data-animation="fadeInUp"
                          >
                            <a
                              href="#"
                              className={`${isActive ? 'bgg-4' : 'bgc-4'} bdr-5 text-center box-hover h-100`}
                              style={{ cursor: 'pointer' }}
                              onClick={(e) => {
                                e.preventDefault()
                                if (notYetOpen) {
                                  toast.error('ยังไม่เปิดรับแทง รอเวลาเปิด')
                                  return
                                }
                                if (isActive) {
                                  navigate('/lottery-terms', { state: { round } })
                                } else {
                                  navigate(`/lottery-result?roundId=${round._id}`, { state: { round } })
                                }
                              }}
                            >
                              <div className={`px-1 py-1 t-regular ${isActive ? 'c-white' : notYetOpen ? 'c-dark' : 'c-red'}`}>
                                {notYetOpen && <small className="d-block" style={{ fontWeight: 600 }}>ยังไม่เปิดรับแทง</small>}
                                <h5><CountdownDisplay round={round} /></h5>
                              </div>
                              <div className={`${isActive ? 'bgc-14' : 'bgg-1'} px-1 t-regular c-white`}>
                                <h4 className="txt-hh">{round.huayName}</h4>
                              </div>
                              <div className={`px-1 py-2 ${isActive ? 'c-white' : 'c-dark'}`}>
                                <small>{notYetOpen ? `เปิดรับ ${openTime}` : `ปิดรับ ${closeTime}`}</small>
                              </div>
                            </a>
                          </div>
                        )
                      })
                    })()
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* หวยต่างประเทศ — ซ่อนทั้ง section เมื่อแอดมินปิดประเภทนี้ */}
          {!disabledHuayTypeNames.has('หวยต่างประเทศ') && (
            <LotterySection
              title="หวยต่างประเทศ"
              icon={getHuayTypeImageSrc(['หวยต่างประเทศ'], 'logo.png')}
            >
              {loading ? (
                <div className="col-12 text-center py-4">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
                  </motion.div>
                </div>
              ) : (
                (roundsByType['หวยต่างประเทศ'] || []).map((round, index) => (
                  <LotteryCard
                    key={round._id}
                    round={round}
                    index={index}
                    navigate={navigate}
                    urgentMinutesThreshold={urgentMinutesThreshold}
                  />
                ))
              )}
              {!loading && (!roundsByType['หวยต่างประเทศ'] || roundsByType['หวยต่างประเทศ'].length === 0) && (
                <motion.div 
                  className="col-12 text-center py-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <p className="c-white">ไม่มีรอบหวยในวันนี้</p>
                </motion.div>
              )}
            </LotterySection>
          )}

          {/* หวยหุ้น VIP — ซ่อนเมื่อปิดประเภท หวยหุ้น VIP หรือ หวย VIP */}
          {!disabledHuayTypeNames.has('หวยหุ้น VIP') && !disabledHuayTypeNames.has('หวย VIP') && (
            <LotterySection
              title="หวยหุ้น VIP"
              icon={getHuayTypeImageSrc(['หวยหุ้น VIP', 'หวย VIP'], 'logo.png')}
            >
              {loading ? (
                <div className="col-12 text-center py-4">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
                  </motion.div>
                </div>
              ) : (
                (roundsByType['หวยหุ้น VIP'] || roundsByType['หวย VIP'] || []).map((round, index) => (
                  <LotteryCard
                    key={round._id}
                    round={round}
                    index={index}
                    navigate={navigate}
                    urgentMinutesThreshold={urgentMinutesThreshold}
                  />
                ))
              )}
              {!loading && (!roundsByType['หวย VIP'] || roundsByType['หวย VIP'].length === 0) && (
                <motion.div 
                  className="col-12 text-center py-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <p className="c-white">ไม่มีรอบหวยในวันนี้</p>
                </motion.div>
              )}
            </LotterySection>
          )}

          {/* หวยหุ้นต่างประเทศ — ซ่อนเมื่อปิดประเภท หวยหุ้น */}
          {!disabledHuayTypeNames.has('หวยหุ้น') && (
            <LotterySection
              title="หวยหุ้นต่างประเทศ"
              icon={getHuayTypeImageSrc(['หวยหุ้น'], 'logo.png')}
            >
              {loading ? (
                <div className="col-12 text-center py-4">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
                  </motion.div>
                </div>
              ) : (
                (roundsByType['หวยหุ้น'] || [])
                  .filter(round => !round.huayName.includes('ไทย'))
                  .map((round, index) => (
                    <LotteryCard
                      key={round._id}
                      round={round}
                      index={index}
                      navigate={navigate}
                      urgentMinutesThreshold={urgentMinutesThreshold}
                    />
                  ))
              )}
              {!loading && (!roundsByType['หวยหุ้น'] || roundsByType['หวยหุ้น'].filter(r => !r.huayName.includes('ไทย')).length === 0) && (
                <motion.div 
                  className="col-12 text-center py-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <p className="c-white">ไม่มีรอบหวยในวันนี้</p>
                </motion.div>
              )}
            </LotterySection>
          )}

          {/* หวยชุด — ซ่อนเมื่อปิดประเภท */}
          {!disabledHuayTypeNames.has('หวยชุด') && (
            <LotterySection
              title="หวยชุด"
              icon={getHuayTypeImageSrc(['หวยชุด'], 'logo.png')}
            >
              {loading ? (
                <div className="col-12 text-center py-4">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
                  </motion.div>
                </div>
              ) : (
                (roundsByType['หวยชุด'] || []).map((round, index) => (
                  <LotteryCard
                    key={round._id}
                    round={round}
                    index={index}
                    navigate={navigate}
                    urgentMinutesThreshold={urgentMinutesThreshold}
                  />
                ))
              )}
              {!loading && (!roundsByType['หวยชุด'] || roundsByType['หวยชุด'].length === 0) && (
                <motion.div 
                  className="col-12 text-center py-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <p className="c-white">ไม่มีรอบหวยในวันนี้</p>
                </motion.div>
              )}
            </LotterySection>
          )}

          {/* หวยหุ้นไทย — ซ่อนเมื่อปิดประเภท หวยหุ้น */}
          {!disabledHuayTypeNames.has('หวยหุ้น') && (
            <LotterySection
              title="หวยหุ้นไทย"
              icon={getHuayTypeImageSrc(['หวยหุ้น'], 'logo.png')}
            >
              {loading ? (
                <div className="col-12 text-center py-4">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
                  </motion.div>
                </div>
              ) : (
                (roundsByType['หวยหุ้น'] || [])
                  .filter(round => round.huayName.includes('ไทย'))
                  .map((round, index) => (
                    <LotteryCard
                      key={round._id}
                      round={round}
                      index={index}
                      navigate={navigate}
                      urgentMinutesThreshold={urgentMinutesThreshold}
                    />
                  ))
              )}
              {!loading && (!roundsByType['หวยหุ้น'] || roundsByType['หวยหุ้น'].filter(r => r.huayName.includes('ไทย')).length === 0) && (
                <motion.div 
                  className="col-12 text-center py-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <p className="c-white">ไม่มีรอบหวยในวันนี้</p>
                </motion.div>
              )}
            </LotterySection>
          )}

          {/* หวย VISA — ซ่อนเมื่อปิดประเภท */}
          {!disabledHuayTypeNames.has('หวย VISA') && (
            <LotterySection
              title="หวย VISA"
              icon={getHuayTypeImageSrc(['หวย VISA'], 'logo.png')}
            >
              {loading ? (
                <div className="col-12 text-center py-4">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
                  </motion.div>
                </div>
              ) : (
                (roundsByType['หวย VISA'] || []).map((round, index) => (
                  <LotteryCard
                    key={round._id}
                    round={round}
                    index={index}
                    navigate={navigate}
                    urgentMinutesThreshold={urgentMinutesThreshold}
                  />
                ))
              )}
              {!loading && (!roundsByType['หวย VISA'] || roundsByType['หวย VISA'].length === 0) && (
                <motion.div 
                  className="col-12 text-center py-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <p className="c-white">ไม่มีรอบหวยในวันนี้</p>
                </motion.div>
              )}
            </LotterySection>
          )}

          {/* ประเภทหวยอื่นๆ จาก API — เพิ่มประเภทใหม่ใน admin จะแสดงที่นี่โดยไม่ต้องแก้ frontend */}
          {otherTypeNames
            .filter((typeName) => !disabledHuayTypeNames.has(typeName))
            .map((typeName) => (
              <LotterySection
                key={typeName}
                title={typeName}
                icon={getHuayTypeImageSrc([typeName], 'logo.png')}
              >
                {loading ? (
                  <div className="col-12 text-center py-4">
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
                      <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
                    </motion.div>
                  </div>
                ) : (
                  (roundsByType[typeName] || []).map((round, index) => (
                    <LotteryCard key={round._id} round={round} index={index} navigate={navigate} urgentMinutesThreshold={urgentMinutesThreshold} />
                  ))
                )}
                {!loading && (!roundsByType[typeName] || roundsByType[typeName].length === 0) && (
                  <motion.div
                    className="col-12 text-center py-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <p className="c-white">ไม่มีรอบหวยในวันนี้</p>
                  </motion.div>
                )}
              </LotterySection>
            ))}
        </div>
      </div>
    </div>
  )
}

export default LottoPage
